import { planMissions } from './missions.js';
import { bandOf, CYBERSCORE_ALGORITHM, EVIDENCE_CONFIDENCE, runCyberScore, scoreBasis, } from './scoring.js';
/**
 * Explainability (tracker task 24). Every stored score carries a trace that says how it was reached: the
 * formula with the assessment's own numbers, each control's credit and the answers and rules behind it,
 * and the points each control earns or would add. Two traces explain a change between scores, part by
 * part, and the parts always add up to the change.
 *
 * Points are marginal changes of the real formula, caps included: "+34 for MFA" is the score with that
 * control fully in place minus the score now. They are not additive, because the formula isn't.
 */
export const EXPLAIN_VERSION = 'explain/v1';
/** A full-credit answer we can't yet verify earns the self-declared confidence. */
const SELF_DECLARED = EVIDENCE_CONFIDENCE.USER_DECLARED;
/** How many improvements a trace lists, best first. */
const MAX_ACTIONS = 10;
const claimsOf = (qs, claims) => {
    const found = qs.filter((q) => claims?.[q]).map((q) => [q, claims[q]]);
    return found.length ? { claims: Object.fromEntries(found) } : {};
};
const r1 = (n) => Math.round(n * 10) / 10;
const r3 = (n) => Math.round(n * 1000) / 1000;
const byKey = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
const label = (s) => s.replace(/_/g, ' ');
/** The CyberScore and its trace. */
export function explainCyberScore(input) {
    const { result, basis, askedBy, answered, credits } = runCyberScore(input);
    const now = scoreBasis(basis);
    const scoreWith = (control, patch) => scoreBasis({
        ...basis,
        controls: basis.controls.map((c) => (c.control === control ? { ...c, ...patch } : c)),
    });
    /** Residual risk each improvement removes, unrounded: orders improvements worth the same points. */
    const gain = new Map();
    const controls = result.control_scores.map((c, i) => {
        const b = basis.controls[i];
        const asked = [...askedBy.get(c.control)];
        const impl = c.implementation;
        const full = scoreWith(c.control, {
            implementation: 1,
            confidence: Math.max(b.confidence, SELF_DECLARED),
        });
        gain.set(c.control, now.residual - full.residual);
        const status = impl === null
            ? asked.some(answered)
                ? 'unknown'
                : 'unanswered'
            : impl >= 1
                ? 'in_place'
                : impl > 0
                    ? 'partial'
                    : 'missing';
        return {
            control: c.control,
            name: input.controls[c.control].name,
            domain: c.domain,
            kill_switch: c.kill_switch,
            status,
            weight: c.weight,
            implementation: impl,
            confidence: c.confidence,
            provenance: c.provenance,
            questions: asked,
            answered: asked.filter(answered),
            credited_by: [...(credits.get(c.control) ?? [])].sort((x, y) => (x.credit ?? -1) - (y.credit ?? -1) || byKey(x.effect, y.effect)),
            earns: impl === null ? 0 : now.score - scoreWith(c.control, { implementation: 0 }).score,
            uplift: impl !== null && impl >= 1 ? 0 : full.score - now.score,
            ...claimsOf(asked.filter(answered), input.claims),
            ...(c.checked ? { checked: true } : {}),
        };
    });
    const actions = controls
        .filter((c) => c.uplift > 0)
        .sort((a, b) => b.uplift - a.uplift || gain.get(b.control) - gain.get(a.control) || byKey(a.control, b.control))
        .slice(0, MAX_ACTIONS)
        .map((c) => ({
        control: c.control,
        name: c.name,
        points: c.uplift,
        lifts_cap: result.kill_switches.failed.some((k) => k.control === c.control),
    }));
    const points = result.inherent.factors.reduce((s, f) => s + f.points, 0);
    const I = r1(basis.inherent);
    const ev = r3(now.evidence);
    const steps = [
        {
            key: 'inherent',
            label: 'Inherent risk',
            expression: `${points} points${result.inherent.multiplier !== 1 ? ` × ${result.inherent.multiplier} (${label(result.inherent.industry ?? '')})` : ''}${points * result.inherent.multiplier > 100 ? ', capped at 100' : ''}`,
            value: I,
        },
        {
            key: 'declared',
            label: 'Control effectiveness, declared',
            expression: 'Σ domain weight × domain score',
            value: r1(now.declared),
        },
        {
            key: 'evidence',
            label: 'Evidence confidence',
            expression: 'Σ weight × credit × confidence ÷ Σ weight × credit',
            value: ev,
        },
        {
            key: 'effective',
            label: 'Evidence-adjusted control effectiveness',
            expression: `${r1(now.declared)} × (0.75 + 0.25 × ${ev})`,
            value: r1(now.effective),
        },
        {
            key: 'residual',
            label: 'Residual risk',
            expression: `${I} × (1 − 0.8 × ${r3(now.effective / 100)})`,
            value: r1(now.residual),
        },
        {
            key: 'uncapped',
            label: 'Score before caps',
            expression: `1000 − 10.5 × ${r1(now.residual)}, within 300–1000`,
            value: now.uncapped,
        },
    ];
    if (now.cap !== null)
        steps.push({
            key: 'cap',
            label: 'Kill-switch cap',
            expression: `${result.kill_switches.failed.map((k) => `${k.control} (${k.name})`).join(', ')} not in place`,
            value: now.cap,
        });
    steps.push({ key: 'score', label: 'CyberScore', expression: now.cap === null ? 'score before caps' : `lower of ${now.uncapped} and ${now.cap}`, value: now.score }); // prettier-ignore
    return {
        result,
        trace: {
            version: EXPLAIN_VERSION,
            algorithm: CYBERSCORE_ALGORITHM,
            score: now.score,
            basis,
            steps,
            inherent: { ...result.inherent, points },
            controls,
            actions,
        },
    };
}
/**
 * Attributes the difference between two scores by changing one input at a time, from the earlier basis to
 * the later one: inherent risk first, then each control in id order. Each part is the score difference its
 * own step makes, so the parts add up to the change exactly, a kill switch's cap included.
 */
export function explainChange(before, after) {
    const name = new Map([...before.controls, ...after.controls].map((c) => [c.control, c.name]));
    const was = new Map(before.basis.controls.map((c) => [c.control, c]));
    const now = new Map(after.basis.controls.map((c) => [c.control, c]));
    let state = new Map(was);
    let inherent = before.basis.inherent;
    const scoreOf = () => scoreBasis({ inherent, controls: [...state.values()] });
    const from = scoreOf();
    let current = from;
    const parts = [];
    let reweighting = { points: 0, controls: 0 };
    if (after.basis.inherent !== inherent) {
        inherent = after.basis.inherent;
        const next = scoreOf();
        parts.push({ kind: 'inherent', points: next.score - current.score, before: r1(before.basis.inherent), after: r1(inherent) }); // prettier-ignore
        current = next;
    }
    for (const id of [...new Set([...was.keys(), ...now.keys()])].sort(byKey)) {
        const a = was.get(id) ?? null;
        const b = now.get(id) ?? null;
        if (a &&
            b &&
            a.weight === b.weight &&
            a.implementation === b.implementation &&
            a.confidence === b.confidence)
            continue;
        state = new Map(state);
        if (b)
            state.set(id, b);
        else
            state.delete(id);
        const next = scoreOf();
        const points = next.score - current.score;
        const reason = !a
            ? 'added'
            : !b
                ? 'removed'
                : a.implementation !== b.implementation
                    ? 'credit'
                    : a.confidence !== b.confidence
                        ? 'evidence'
                        : null;
        if (reason === null) {
            reweighting = { points: reweighting.points + points, controls: reweighting.controls + 1 };
        }
        else if (points !== 0) {
            const pick = (c) => c && { implementation: c.implementation, confidence: c.confidence };
            parts.push({
                kind: 'control',
                control: id,
                name: name.get(id) ?? id,
                reason,
                points,
                before: pick(a),
                after: pick(b),
                ...(current.cap !== next.cap ? { cap: { before: current.cap, after: next.cap } } : {}),
            });
        }
        current = next;
    }
    if (reweighting.points !== 0)
        parts.push({ kind: 'reweighting', ...reweighting });
    parts.sort((x, y) => Math.abs(y.points) - Math.abs(x.points) || partKey(x).localeCompare(partKey(y)));
    return { from: from.score, to: current.score, delta: current.score - from.score, parts };
}
const partKey = (p) => (p.kind === 'control' ? p.control : p.kind);
const REASON = {
    credit: 'answers changed',
    evidence: 'evidence changed',
    added: 'now applies',
    removed: 'no longer applies',
};
/** A change part in words, for the explainer agent. */
export function changePartLabel(p) {
    if (p.kind === 'inherent')
        return `Business exposure (inherent risk ${p.before} → ${p.after})`;
    if (p.kind === 'reweighting')
        return `Other controls reweighted (${p.controls})`;
    return `${p.name} (${p.control}): ${REASON[p.reason]}`;
}
/**
 * What the explainer agent may say about a score (decision 0013): the score, its cap and why, the best
 * next steps and what moved it last, all from the stored trace. The agent may state no other score facts.
 */
export function agentScoreContext(trace, provisional, change) {
    return {
        score: trace.score,
        // In words, as the glossary names the bands: "Very good".
        band: bandOf(trace.score).charAt(0) + bandOf(trace.score).slice(1).toLowerCase().replace('_', ' '),
        provisional,
        cap: trace.steps.find((s) => s.key === 'cap')?.value ?? null,
        cap_reasons: trace.controls.filter((c) => c.kill_switch && c.implementation === 0).map((c) => c.control),
        top_actions: trace.actions.slice(0, 5).map(({ control, name, points }) => ({ control, name, points })),
        top_missions: planMissions(trace)
            .filter((m) => m.points > 0)
            .slice(0, 3)
            .map((m) => ({ id: m.id, name: m.name, points: m.points, next_points: m.next.points, steps: m.next.steps.map((s) => s.control) })), // prettier-ignore
        last_change: change && {
            from_score: change.from,
            to_score: change.to,
            delta: change.delta,
            parts: change.parts.slice(0, 5).map((p) => ({ label: changePartLabel(p), points: p.points })),
        },
    };
}
//# sourceMappingURL=explain.js.map